import * as a from '@mitranim/js/all.mjs'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as dg from '@mitranim/js/dom_global'
import {A, E} from './util.mjs'
import * as u from './util.mjs'
import * as c from './conf.mjs'
import * as d from './dat.mjs'
import * as n from './net.mjs'
import * as eb from './elem-btn.mjs'
import * as ef from './elem-form.mjs'
import * as eim from './elem-input-md.mjs'
import * as em from './elem-misc.mjs'
import * as s from './views/svg.mjs' // TODO use async import.

const ID_COMMENTS = `comments`
const MSG_PM_ITEM_CLS = `col-cen-sta text-left w-full pad-sm gap anim`
const MSG_PM_ITEM_THEME = `round`

function renderRealTime({date, pos, class: cls}) {
  return a.vac(date) && E(
    `span`,
    A
      .cls(`fg-graphite`).cls(cls)
      .set(`aria-label`, date)
      .set(`data-label-pos`, pos),
    u.relTime(Date.now(), date),
    ` ago`,
  )
}

/*
TODO:

  * Support "load newer" via dedicated button that's always visible.
  * If the URL has a msg id or timestamp in its hash, scroll to that msg.
*/
export class Msgs extends u.MixElemReinit(u.Elem) {
  // Intersection observer created in `.connectedCallback`.
  obs = undefined

  get A() {return A.cls(`msgs`).with(this.getDescProps()).test(`MessagesBody`)}
  title() {return `Messages`}
  keyNext() {return `next`}
  keyPrev() {return `prev`}
  keyTop() {return `top`}
  enableKeyStorage() {return true}
  queryKey() {return a.vac(this.enableKeyStorage()) && this.ctx.url.query.get(c.QUERY_KEY_KEY)}
  searchText() {return this.ctx.url.query.get(c.SEARCH_TEXT_KEY)}
  pageInp(val) {return new d.MsgPageInp(val).mut({searchText: this.searchText()})}
  actInp() {throw u.errImplementInSubclass()}
  pageNext() {return this.buk().get?.pages?.msgs?.get(this.keyNext())}
  pagePrev() {return this.buk().get?.pages?.msgs?.get(this.keyPrev())}
  msgs() {return this.descs(Msg)}
  formPlaceholderText() {return `What would you like to share?`}
  listPlaceholderText() {return `Be the first to post a message!`}
  canCreate() {return true}
  threadAuthor() {}
  prevBtnClickLax() {u.clickNotHidden(this.desc(MsgBtnFetchPrev))}
  nextBtnClickLax() {u.clickNotHidden(this.desc(MsgBtnFetchNext))}
  enableInlineServices() {}

  get FormTop() {return MsgFormCreateTop}
  get TopDescription() {return undefined}

  get List() {return MsgList}
  list() {return this.desc(this.List)}

  get TopControl() {return MsgControlTop}
  topControl() {return this.desc(this.TopControl)}

  get BottomControl() {return MsgControlBottom}
  bottomControl() {return this.desc(this.BottomControl)}

  get FormBottom() {return MsgFormCreateBottom}
  formBottom() {return this.desc(this.FormBottom)}

  // Storing this in the dataset allows it to be consistent between SSR and SPA.
  // After loading the page and adding new messages, both SSR-rendered and
  // SPA-appended messages should use the same gallery.
  galleryId() {
    return this.dataset.galleryId ||= u.makeGalleryId(this.ctx.seq.nextId())
  }

  addMsgPageInpOpt(inp, src) {
    a.reqInst(inp, d.BukInp)
    if (a.isNil(src)) return

    if (this.enableInlineServices()) {
      inp.get.pages.addMsgsWithServices(src)
      inp.get.sets.services.enable()

      // Probably don't need.
      // inp.get.sets.serviceExts.enable()

      // Service aggs are required for image files.
      inp.get.sets.serviceAggs.enable()
    }
    else {
      inp.get.pages.msgs.add(src)
    }

    d.Msg.mutInpSets(inp)
  }

  pageInpPrevInitial() {
    const from = this.queryKey()
    if (!from) return undefined

    return this
      .pageInp({key: this.keyPrev(), limit: 1})
      .align({desc: !this.getDesc(), from})
  }

  addPageInpPrevInitial(inp) {
    this.addMsgPageInpOpt(inp, this.pageInpPrevInitial())
  }

  pageInpNextInitial() {
    return this
      .pageInp({key: this.keyNext()})
      .align({desc: this.getDesc(), from: this.queryKey(), exact: true})
  }

  addPageInpNextInitial(inp) {
    this.addMsgPageInpOpt(inp, this.pageInpNextInitial())
  }

  /*
  This request is tricky. If the current page shows items in descending order
  (newer to older), then the previous page must be in ascending order
  (older to newer), starting after the timestamp of the current first item.
  Conversely, if the current page shows items in ascending order (older to
  newer), then the previous page must be in descending order (newer to
  older), starting before the timestamp of the current first item. After
  fetching, we must reverse items because `.pagePrepend` expects normal
  ordering.
  */
  pageInpPrevAdditional() {
    return this.pageInp({key: this.keyPrev()}).align({
      desc: !this.getDesc(),
      from: this.list().msgHead()?.key(),
    })
  }

  addPageInpPrevAdditional(inp) {
    this.addMsgPageInpOpt(inp, this.pageInpPrevAdditional())
  }

  pageInpNextAdditional() {
    return this.pageInp({key: this.keyNext()}).align({
      desc: this.getDesc(),
      from: this.list().msgLast()?.key(),
    })
  }

  addPageInpNextAdditional(inp) {
    this.addMsgPageInpOpt(inp, this.pageInpNextAdditional())
  }

  pageDatInp() {return Msg.mutInp(this.pageDat()?.inp)}

  preinit() {
    const inp = this.pageDatInp()
    this.addPageInpPrevInitial(inp)
    this.addPageInpNextInitial(inp)
  }

  async init() {
    const desc = this.getDesc()
    const list = (await this.idemp(this.List).setFlash(false).pageReplace(this.pageNext())).reinit()

    return E(this, this.A,
      this.TopDescription?.(),
      a.vac(this.canCreate() && desc) && this.idemp(this.FormTop),
      this.idemp(this.TopControl).updateByPage(this.pagePrev()),
      list,
      this.idemp(this.BottomControl).updateByPage(this.pageNext()),
      a.vac(this.canCreate() && !desc) && this.idemp(this.FormBottom),
    )
  }

  // Note: desc = new-to-old, from bigger/later to smaller/earlier timestamps.
  getDesc() {return this.getDescForced() ?? this.bank().bitsHas(c.DISK_BITS_MSG_ORDER_DESC)}
  setDesc(val) {return this.bank().bitsSwitch(c.DISK_BITS_MSG_ORDER_DESC, val).write(), this}
  getDescProps() {return A.sortDesc(this.getDesc())}
  getDescForced() {}
  canSort() {return a.isNil(this.getDescForced())}

  updateKey(val) {
    if (c.DOM_AVAILABLE && this.enableKeyStorage()) {
      u.Loc.replaceKey(c.QUERY_KEY_KEY, val)
    }
  }

  connectedCallback() {
    super.connectedCallback?.()
    if (this.enableKeyStorage()) {
      this.obs?.disconnect()
      this.obs = (
        a.vac(c.INTERSECTION_AVAILABLE) &&
        new u.InterObs(this, {threshold: [0, 1]})
      )
    }
    this.hotkeyInit()
  }

  disconnectedCallback() {
    this.hotkeyDeinit()
    this.obs?.disconnect()
    super.disconnectedCallback?.()
  }

  handleEvent(eve) {
    if (eve.type === `keydown`) {
      if (eve.key === c.KEY_ARROW_UP && !u.isEventModified(eve)) {
        a.eventKill(eve)
        this.refocusMsg(a.prevSibling, u.listPrev, this.prevBtnClickLax)
        return
      }
      if (eve.key === c.KEY_ARROW_DOWN && !u.isEventModified(eve)) {
        a.eventKill(eve)
        this.refocusMsg(a.nextSibling, u.listNext, this.nextBtnClickLax)
        return
      }
    }
  }

  hotkeyInit() {
    this.unlisten?.()
    this.unlisten = this.env.events.addEvent(`keydown`, this)
  }

  hotkeyDeinit() {
    this.unlisten?.()
    this.unlisten = undefined
  }

  // Should be triggered either by manual button press, or by scrolling up.
  async fetchPrev() {
    const inp = Msg.inp()
    this.addPageInpPrevAdditional(inp)
    const buk = await this.pageDat().bukMut(inp)
    const page = buk.get.pages.msgs.get(this.keyPrev())
    page.reverse()
    const list = this.list()
    void (await list.setFlash(false).pagePrepend(page)).reinit()
    this.topControl()?.updateByPage(page)
    return this
  }

  // Should be triggered either by manual button press, or by scrolling down.
  async fetchNext() {
    const inp = Msg.inp()
    this.addPageInpNextAdditional(inp)

    await this.pageDat().bukMut(inp)
    const page = this.pageNext()
    const list = this.list()

    void (await list.setFlash(false).pageAppend(page)).reinit()
    this.bottomControl()?.updateByPage(page)
    return this
  }

  async reorder() {
    this.setDesc(!this.getDesc())
    await this.reinit()
  }

  edgeMsgKey() {
    return (
      this.getDesc()
      ? this.list().msgHead()?.key()
      : this.list().msgLast()?.key()
    )
  }

  inpForCreate(src) {
    const inp = new d.BukInp()
    Msg.mutInp(inp)
    inp.user()
    inp.act.msgs.create = [{...this.actInp(), ...a.reqDict(src)}]
    return inp
  }

  /*
  This handles 4 cases:

    * DD: list = desc (new-to-old), form = desc (above).
    * DA: list = desc (new-to-old), form = asc  (below).
    * AD: list = asc  (old-to-new), form = desc (above).
    * AA: list = asc  (old-to-new), form = asc  (below).

  Behavior:

    * DD: prepend new msgs at the top.
    * DA: change list order to asc, replace with page of recent msgs.
    * AD: change list order to desc, replace with page of recent msgs.
    * AA: append new msgs at the bottom.
  */
  async create(form, src) {
    if (!a.reqDict(src).content) return undefined
    if (form.getDesc() === this.getDesc()) return this.createStraight(form, src)
    return this.createReverse(form, src)
  }

  /*
  Must be used when creating a message via a form positioned at the "end" of the
  message feed, near the newest message. When the feed is old-to-new, the form
  should be at the bottom. When the feed is new-to-old, the form should be at
  the top.
  */
  async createStraight(form, src) {
    const key = this.keyNext()
    const inp = this.inpForCreate(src)

    this.addMsgPageInpOpt(inp, this.pageInp({key}).align({
      desc: true,
      to: this.edgeMsgKey(),
    }))

    await this.pageDat().bukMut(inp)
    const page = this.buk().get.pages.msgs.get(key)
    const list = this.list()

    list.setFlash(true)
    if (this.getDesc()) {
      void (await this.list().pagePrepend(page)).reinit()
      this.topControl()?.updateByPage()
    }
    else {
      void (await this.list().pageAppend(page.reverse())).reinit()
      this.bottomControl()?.updateByPage()
    }
    form.reset()
  }

  /*
  Must be used when creating a message via a form positioned at the "start" of
  the message feed, near the oldest message. When the feed is old-to-new, the
  form should be at the top. When the feed is new-to-old, the form should be at
  the bottom.

  Unlike `.createStraight`, this re-requests and replaces the entire list of
  messages. May reconsider.
  */
  async createReverse(form, src) {
    const key = this.keyNext()
    const inp = this.inpForCreate(src)
    this.addMsgPageInpOpt(inp, this.pageInp({key}))

    await this.pageDat().bukMut(inp)
    const page = this.buk().get.pages.msgs.get(key)

    const desc = form.getDesc()
    if (!desc) page.reverse()

    this.setDesc(desc)
    E(this, A.sortDesc(desc))

    const list = this.list()
    await list.setFlash(true).pageReplace(page)
    list.reinit()
    this.topControl()?.updateByPage()
    this.bottomControl()?.updateByPage()
    form.reset()
  }

  // Called by `InterObs`.
  intersect(val) {
    const node = a.find(val, u.intersectionEntryIsIntersectingWithViewportTop)?.target
    if (!node) return

    if (a.ancestor(node, MsgList)) {
      const key = node?.key?.()
      if (key) this.updateKey(key)
      return
    }

    this.topControl()?.updateKey()
  }

  showSubjectPreview() {}

  refocusMsg(getSibling, getElem, loadMore) {
    // Common case, efficient.
    const older = focusedMsg()
    if (!older) return this.desc(Msg)?.focus()

    // Common case, efficient.
    const newer = getSibling(older, Msg)
    if (newer) return newer.focus()

    // Uncommon case, inefficient but acceptable.
    const msgs = u.selAll(this, dr.reg(Msg).localName)
    const elem = getElem(msgs, older)
    if (elem) return elem.focus()

    return loadMore?.call(this)
  }
}

function MixMsgElem(cls) {
  return class MixMsgElemCls extends cls {
    msg() {return this.anc(Msg)}
    msgs() {return this.anc(Msgs)}
    subjective() {return this.findAnc(isSubjective)}
  }
}

function MixMsgElemObs(cls) {
  return class MixMsgElemObsCls extends MixMsgElem(cls) {
    obs() {return this.msgs()?.obs}
    observe() {this.obs()?.observe(this)}
    unobserve() {this.obs()?.unobserve(this)}

    connectedCallback() {
      super.connectedCallback?.()
      this.observe()
    }

    disconnectedCallback() {
      try {this.unobserve()}
      finally {super.disconnectedCallback?.()}
    }
  }
}

// Why this is only for `Msgs`? Probably can be named as MixUserPreinit
export function MixUserMsgs(cls) {
  return class MixUserMsgs extends cls {
    async preinit(...val) {
      // TODO: prefer `window.process.env.user` on the client,
      // to avoid this request on some pages.
      await d.requireUser(this.ctx)
      await super.preinit(...val)
    }

    user() {return this.ctx.dat.user.value}
  }
}

export class GlobalMsgs extends Msgs {
  static {dr.reg(this)}

  pageInp(val) {
    return super.pageInp(val).mut({
      chanIds: [c.GLOBAL_CHAN_ID],
      type: c.MSG_TYPE_PUBLIC,
      lastInChannel: true,
    })
  }
  actInp() {return {chanId: c.GLOBAL_CHAN_ID}}
  getDescForced() {return true}

  showSubjectPreview(chanId) {
    return !!a.optPk(chanId) && chanId !== c.GLOBAL_CHAN_ID
  }
}

export class FollowChanMsgs extends Msgs {
  static {dr.reg(this)}
  title() {return `Following`}
  pageInp(val) {return super.pageInp(val).mut({subs: true, fromChannel: true})}
  canCreate() {return false}
  getDescForced() {return true}
}

export class MyMsgs extends MixUserMsgs(Msgs) {
  static {dr.reg(this)}
  title() {return `My Posts`}
  pageInp(val) {return super.pageInp(val).mut({personIds: [a.pk(this.user())]})}
  actInp() {return {chanId: a.pk(this.user())}}
}

export class ReplyMsgs extends MixUserMsgs(Msgs) {
  static {dr.reg(this)}
  title() {return `Replies`}
  pageInp(val) {return super.pageInp(val).mut({own: true})}
  canCreate() {return false}
  listPlaceholderText() {return `This message feed is empty for now`}
  showSubjectPreview() {return true}
  getDescForced() {return true}
}

export class MentionMsgs extends MixUserMsgs(Msgs) {
  static {dr.reg(this)}
  title() {return `Mentions`}
  pageInp(val) {return super.pageInp(val).mut({mentions: true})}
  canCreate() {return false}
  listPlaceholderText() {return `You haven't been @mentioned yet. Go and make waves!`}
  showSubjectPreview() {return true}
  getDescForced() {return true}
}

class MsgThreadBase extends Msgs {
  pk() {return a.reqPk(this.pkOpt())}
  pkOpt() {return this.dataset.chanId}
  getType() {throw u.errImplementInSubclass()}

  async preinit(id) {
    if (a.isSome(id)) this.dataset.chanId = a.reqValidStr(id)
    else a.pk(this)
    await super.preinit()
  }

  pageInp(val) {
    return super.pageInp(val).mut({
      chanIds: [a.pk(this)], expand: true, type: this.getType(),
    })
  }

  actInp() {return {chanId: a.pk(this), type: this.getType()}}
}

export class ModerMsgs extends MsgThreadBase {
  static {dr.reg(this)}
  title() {return `Moderation Messages`}
  getType() {return c.MSG_TYPE_MODERATION}
  getDescForced() {return true}
  formPlaceholderText() {return `Message text here`}
}

/*
TODO:

  * Error 404 when message is not found.
  * Fetch the parent of the top msg. Render above, like on Twitter.
    * Collapsed/short.
    * Link to parent.
*/
export class MsgThread extends MsgThreadBase {
  static {dr.reg(this)}

  title() {return `Thread`}
  getType() {return c.MSG_TYPE_PUBLIC}
  formPlaceholderText() {return `Write reply (submit with Ctrl+Enter)`}
  getDesc() {return super.getDesc() ?? false}
  threadMsg() {return this.buk().msg(this.pkOpt())}
  threadAuthor() {return this.buk().person(this.threadMsg()?.personId)}
  enableInlineServices() {return true}

  async preinit(id = a.pk(this)) {
    a.reqValidStr(id)
    await super.preinit(id)
    this.pageDat().inp.get.sets.chans = {msgIds: [id]}
  }

  async init() {
    await super.init()
    return this.prependChi(this.ancestorMsgs())
  }

  // TODO: 404 if ancestor not found.
  ancestorMsgs() {
    return new AncestorMsgs(this)
      .init(this.buk().get.sets.msgs?.ancestorIds(this.pk()).reverse())
      .vac()
  }

  showSubjectPreview(chanId) {
    return !!a.optPk(chanId) && chanId !== this.pk()
  }
}

export class MsgThreadComments extends MsgThread {
  static {dr.reg(this)}
  getDescForced() {return true}
}

export class MsgThreadRow extends MixUserMsgs(em.Link) {
  static {dr.reg(this)}

  personId(userId, msg) {
    return msg.chanId === userId ? msg.personId : msg.chanId
  }

  async init({id, class: cls}) {
    const parts = await u.imp(import(`./views/parts.mjs`))
    const userId = this.buk().get.units.personId
    const msg = this.buk().msg(id)
    const person = this.buk().person(this.personId(userId, msg))
    const title = d.guessPersonName(person)
    const imageUrl = u.imageUrl(d.guessPersonImage(person), c.IMAGE_WIDTH_SMALL) || c.AVATAR_PLACEHOLDER

    const preview = E(`div`, A.cls(`msg-preview`))
    preview.innerHTML = u.mdToHtml(msg.content)

    return E(
      this,
      A
        .cls(MSG_PM_ITEM_CLS)
        .cls(cls || `brd-1 busy-bg-light-fg-dark`)
        .href(a.toUrl(c.LINK_MSGS_PM(person.id))),
      parts.TitledAvatar({title, imageUrl, chi: preview}),
    )
  }
}

export class PersonalMsgs extends MixUserMsgs(u.Elem) {
  static {dr.reg(this)}

  get A() {return A.cls(`col-sta-sta w-full gap`)}
  title() {return `Personal Messages`}
  pageKey() {return c.BUK_KEY_MAIN}
  getType() {return c.MSG_TYPE_PERSONAL}

  preinit() {
    const inp = this.pageDat().inp
    inp.get.pages.msgs.add({
      key: this.pageKey(),
      type: this.getType(),
      expand: true,
      lastInChannel: true
    })
    inp.user()
    d.Msg.mutInpSets(inp)
  }

  threadAuthor() {return this.user()}

  async init() {
    const page = this.buk().get.pages.msgs.get(this.pageKey())
    const items = page?.getItems()
    return E(this, this.A, await u.PromiseList.map(items, ({id}) => new MsgThreadRow(this).init({
      id,
      class: u.cls(MSG_PM_ITEM_THEME, `hov-shad-std`),
    })))
  }
}

export class MsgThreadPersonal extends MixUserMsgs(MsgThreadComments) {
  static {dr.reg(this)}

  title() {return `Personal Messaging`}
  getType() {return c.MSG_TYPE_PERSONAL}
  threadMsg() {}
  showSubjectPreview() {}
  enableInlineServices() {}
  ancestorMsgs() {}

  async preinit(id = a.pk(this)) {
    a.reqValidStr(id)
    await super.preinit(id)

    const inp = this.pageDat().inp
    inp.get.sets.persons.add(id)
    inp.user()
    d.Msg.mutInpSets(inp)
  }

  // TODO Deduplicate with other same snippets.
  async init() {
    await super.init()

    const parts = await u.imp(import(`./views/parts.mjs`))
    const person = this.buk().person(this.pk())
    const title = d.guessPersonName(person)
    const imageUrl = u.imageUrl(d.guessPersonImage(person), c.IMAGE_WIDTH_SMALL) || c.AVATAR_PLACEHOLDER

    return this.prependChi(
      E(`div`, A.cls(MSG_PM_ITEM_CLS).cls(MSG_PM_ITEM_THEME),
        parts.TitledAvatar({title, imageUrl, class: `row-sta-cen wei-bold w-full gap-sm`}),
      )
    )
  }
}

export class MsgThreadEmentor extends MixUserMsgs(MsgThreadPersonal) {
  static {dr.reg(this)}

  get A() {return super.A.cls(`maw-col-8`)}
  title() {return `Private E-Mentor`}
  pkOpt() {return c.PERSON_ID_EMENTOR}

  listPlaceholderText() {return null}
}

class AncestorMsgs extends u.Elem {
  static {dr.reg(this)}

  get A() {return A.cls(`col-sta-cen w-full`)}

  init(ids) {
    return E(this, this.A, u.intersperse(
      a.map(ids, id => new Msg(this).init(id)),
      MsgConnection,
    ))
  }

  // TODO show subject only for root ancestor.
  showSubjectPreview() {return true}
}

class MsgServices extends u.Elem {
  static {dr.reg(this)}

  init(key) {
    const buk = this.buk()
    const page = buk.get.pages.msgServices.get(key)
    if (!page?.hasItems()) return undefined

    const services = a.map(page.getEnts(), item => buk.service(u.pk(item)))
    return this.draw(services)
  }

  initWithServices(services) {return this.draw(services)}

  async draw(services) {
    if (a.isEmpty(services)) return undefined

    const crd = await u.imp(import(`./views/cards.mjs`))

    return E(
      this,
      A.cls(`msg-services`),
      a.map(services, service => crd.ServiceCard({
        service,
        smallCard: true,
        isNegPos: true,
      })),
    )
  }

  disconnectedCallback() {u.preactClear(this)}
}

class MsgList extends MixMsgElem(u.Elem) {
  static {dr.reg(this)}

  init() {return E(this, A.cls(`msg-list`))}

  reinit() {
    this.classList.toggle(`sort-gradient`, this.hasMsgs())

    if (this.hasMsgs()) this.placeholder()?.remove()
    else E(this, {}, this.idemp(MsgPlaceholder))

    return this
  }

  // Switches the list into "flash mode" for newly added msgs.
  flash = false
  setFlash(val) {return this.flash = a.reqBool(val), this}

  msgs() {return this.descs(Msg)}
  msgHead() {return a.head(this.msgs())}
  msgLast() {return a.last(this.msgs())}
  hasMsgs() {return a.isSome(this.msgHead())}
  placeholder() {return this.desc(MsgPlaceholder)}
  get Msg() {return Msg}
  toMsg(val) {return new this.Msg(this).init(u.pk(val)).setFlash(this.flash)}

  async pageReplace(page) {return E(this, {}, await this.pageToMsgs(page))}
  async pagePrepend(page) {return this.prependChi(await this.pageToMsgs(page))}
  async pageAppend(page) {return this.appendChi(await this.pageToMsgs(page))}

  // Async due to dynamic async imports.
  pageToMsgs(page) {
    if (a.isNil(page)) return undefined

    const msgs = a.values(page?.getItems()).map(this.toMsg, this)
    const servicePage = this.buk().get.pages.msgServices?.get(page.key)
    const services = a.values(servicePage?.getEnts())

    if (a.isEmpty(services)) return msgs

    return Promise.all(u.intersperseN(msgs, c.AFTER_N_MESSAGE_PROMO, ind => (
      new MsgServices(this).initWithServices(u.chunkAt(
        services, c.SERVICES_IN_MSGS_PER_PROMO, ind
      ))
    )))
  }
}

export class NotifMsgList extends MsgList {
  static {dr.reg(this)}
  get Msg() {return NotifMsg}
}

class Msg extends MixMsgElemObs(u.Elem) {
  static {dr.reg(this)}

  static inp() {return this.mutInp(new d.BukInp())}

  static mutInp(inp) {
    this.mutBase(inp)
    this.mutFoot(inp)
    this.mutBody(inp)
    return inp
  }

  static mutBase(inp) {
    // Required for `Msg..canControl`.
    inp.userId()

    // Required for `MsgAva`.
    inp.get.sets.files.enable()

    return inp
  }

  static mutFoot(inp) {return MsgFoot.mutInp(inp)}
  static mutBody(inp) {return MsgBody.mutInp(inp)}

  ent(id) {return this.buk().msg(id)}
  sitePath() {return this.customSitePath || this.ent(this.pk()).sitePath()}

  setSitePath(val) {
    this.customSitePath = a.reqStr(val)
    E(this, A.fakeLink(val)) // other place?
  }

  get cls() {return `msg`}

  A(ent) {
    return A
      .cls(this.cls)
      .id(a.pk(ent))
      .fakeLink(ent.sitePath())
      .dataset({
        key: ent.createdAt,
        content: a.vac(ent.canControl()) && ent.content,
      })
  }

  init(id) {
    a.reqPk(id)
    const ent = this.ent(id)

    E(this, this.A(ent))
    this.draw(ent)
    if (ent.deletedAt) this.deleted()
    return this
  }

  draw(ent) {E(this, {}, MsgAva(ent), new MsgMain(this).init(ent))}
  pk() {return this.id}
  key() {return this.dataset.key}
  main() {return this.desc(MsgMain)}
  body() {return this.main()?.body()}
  content() {return this.dataset.content}

  connectedCallback() {
    super.connectedCallback?.()
    this.hotkeyInit()
  }

  hotkeyInit() {
    this.onmouseenter = this.onclick = this.onkeydown = this.handleEvent
  }

  hotkeyDeinit() {
    this.onmouseenter = this.onclick = this.onkeydown = null
  }

  handleEvent(eve) {
    switch (eve.type) {
      case `mouseenter`: return u.maybeFocus(this) && a.eventKill(eve)
      case `click`: return u.fakeLinkOnClick.call(this, eve)
      case `keydown`: switch (eve.key) {
        case c.KEY_ENTER: return u.fakeLinkOnClick.call(this, eve)
        case `c`: return a.eventKill(eve), this.onKeyComments(eve)
        case `s`: return a.eventKill(eve), this.onKeyShare(eve)
        case `t`: return a.eventKill(eve), this.onKeyReply(eve)
        case `r`: return a.eventKill(eve), this.onKeyEmoji(eve)
        case `e`: return a.eventKill(eve), this.onKeyEdit(eve)
        case c.KEY_DELETE: return a.eventKill(eve), this.onKeyDelete(eve)
        case c.KEY_BACKSPACE: return a.eventKill(eve), this.onKeyDelete(eve)
      }
    }
    return undefined
  }

  onKeyComments() {this.desc(MsgComments).click()}
  onKeyShare() {this.desc(MsgShare).toggle()}
  onKeyReply() {throw a.errImpl()}
  onKeyEmoji() {this.desc(MsgFoot).toggleMarks()}
  onKeyEdit() {this.drawEdit()}
  onKeyDelete() {this.delete().catch(em.onErr)}

  drawEdit() {
    this.main().drawEdit()
    this.hotkeyDeinit()
  }

  restoreView() {if (this.main().restoreView()) this.reset()}
  resetView(ent) {this.main().resetView(ent), this.reset()}
  reset() {this.hotkeyInit(), this.focus()}

  async update(form, src) {
    // TODO consider: empty content = delete message, like in Discord.
    if (!a.reqDict(src).content) return

    const id = a.pk(this)
    const inp = new d.BukInp()
    this.constructor.mutInp(inp)
    inp.act.msgs.update = [{...a.reqDict(src), id}]
    inp.get.sets.msgs.add(id)

    await this.pageDat().bukMut(inp)
    this.init(id)
  }

  // TODO: avoid redundant requests when this event is triggered via hotkey.
  async delete() {
    const preview = u.ellMid(this.body()?.textContent)

    if (!window.confirm(u.joinParagraphs(
      a.spaced(
        `Deleted messages can't be recovered. Really delete?`,
        preview && `Preview:`,
      ),
      preview,
    ))) {
      return
    }

    await n.fetchBuk(this.ctx, {
      act: {msgs: {delete: [{id: a.pk(this)}]}},
    })
    this.deleted()
  }

  /*
  Intent: prevent all further interactivity. TODO simplify and improve.

  Known issues:

    * `pointer-events: none` DOES NOT fully disable interactivity, because it
      doesn't interfere with keyboard controls. Inner controls may still
      receive focus on Tab, and can be activated with Enter.

    * A click-capturing event listener doesn't seem to intercept "blank" links.
  */
  deleted() {
    this.setAttribute(`disabled`, `true`)
    this.classList.add(`pointer-none`)
    if (c.DOM_AVAILABLE) this.addEventListener(`click`, a.eventKill, u.CAPTURE)
    u.nodeEach(this, u.disable)
    this.body()?.deleted()
  }

  setFlash(val) {return u.elemFlashToggle(this, val)}
}

class NotifMsg extends Msg {
  static {dr.reg(this)}

  static mutInp(inp) {
    this.mutBody(inp)
    return inp
  }

  get cls() {return `msg --no-pad`}

  draw(ent) {E(this, {}, new NotifMsgMain(this).init(ent))}
}

function focusedMsg() {return a.ancestor(document.activeElement, Msg)}

/*
TODO:

  * First letter(s) of the name.
  * Random different colors.
*/
function MsgAva(msg) {
  return E(`span`, A
    .cls(`avatar --md`)
    .bgUrl(msg.author()?.avatarImage?.url(c.IMAGE_WIDTH_SMALL))
  )
}

class MsgMain extends u.Elem {
  static {dr.reg(this)}

  init(ent) {return E(this, A.cls(`msg-main`)).resetView(ent)}
  head() {return this.desc(MsgHead)}
  body() {return this.desc(MsgBody)}
  foot() {return this.desc(MsgFoot)}
  form() {return this.desc(MsgForm)}

  /*
  Implementation note. When switching to "edit mode", we stash "view mode"
  children, and when switching back to "view mode", we restore them. This is
  done because we can't re-create "view mode" children without the original
  data, which is unavailable on the client after loading a server-rendered
  page.
  */
  childNodesPrev = undefined

  resetView(ent) {
    return E(this, {},
      new MsgHead(this).init(ent),
      new MsgBody(this).init(ent),
      new MsgFoot(this).init(),
    )
  }

  restoreView() {
    return !!this.form().discardChanges() && (E(this, {}, this.childNodesPrev), true)
  }

  drawEdit() {
    this.childNodesPrev = a.arr(this.childNodes)
    return E(this, {}, this.head(), new MsgFormEdit(this).init())
  }
}

export class NotifMsgMain extends MsgMain {
  static {dr.reg(this)}

  resetView(ent) {
    const date = a.toInstOpt(ent.createdAt, u.DateTimeHuman)

    return E(this, {},
      renderRealTime({date, pos: `bl`}),
      new MsgBody(this).init(ent)
    )
  }
}

class MsgHead extends MixMsgElem(u.Elem) {
  static {dr.reg(this)}

  init(ent) {
    a.reqInst(ent, d.Msg)
    const author = ent.author()
    const date = a.toInstOpt(ent.createdAt, u.DateTimeHuman)
    const preview = this.subjective()?.showSubjectPreview(ent.chanId)
    const nameOrLink = guessPersonNameOrLink(author)

    return E(
      this,
      A.cls(`msg-head`),
      E(`div`, A.cls(`msg-head-main`),
        a.vac(nameOrLink) && E(`span`, A.cls(`msg-head-author`),
          nameOrLink,
          PersonIndicator(author, this.buk().userOpt()),
        ),
        MsgHeadModer(ent),
        E(`span`, A.cls(`fg-graphite`), `•`),
        renderRealTime({date, pos: `cen`}),
        a.vac(preview) && MsgSubjPreview(ent),
      )
    )
  }
}

function PersonIndicator(person, user) {
  if (u.teq(person?.id, user?.id)) {
    return Tooltip(`you`, ` ⭐️`)
  }

  if (person?.isModerator && user?.isModerator) {
    return Tooltip(`moderator like you`, ` ⚡️`)
  }

  return undefined
}

function Tooltip(tip, ...chi) {
  return E(`span`, A.tooltip(tip).cls(`cur-help`), ...chi)
}

function MsgHeadModer(ent) {
  a.reqInst(ent, d.Msg)
  if (ent.type !== c.MSG_TYPE_MODERATION) return undefined

  return E(
    `span`,
    A
      .tooltip(`visible only to moderators and you`)
      .cls(`cur-help pad-h-0x25 round bg-accent-light`),
    `moderation`,
  )
}

function MsgSubjPreview(msg) {
  const pagePath = msg.subjPagePath()
  const title = msg.subjTitle()
  const type = msg.subjType()

  return a.vac(!msg.isPersonalChannel()) && [
    a.vac(type) && E(`span`, A.cls(`fg-warm-gray`), type),
    /*
    TODO avoid HTML escaping here. Example of redundant escaping:
    https://corespirit.com/msgs/65772d263b684d0d9d39008b9437fb02
    */
    pagePath ? SubjLink({href: pagePath}, title) : title,
  ]
}

function SubjLink({href, cls}, ...chi) {
  return a.vac(chi) && E(
    new em.Link(),
    A.href(href).cls(`msg-subj-link`).cls(cls),
    chi,
  )
}

function guessPersonNameOrLink(person) {
  const name = emailNameOnly(person?.getName())
  if (!d.isPractModerationStatusApproved(person)) return name
  return SubjLink({href: d.practLink(person)}, name)
}

/*
TODO: instead of displaying only the part before the `@`, include `@` with a
truncated domain. For addresses with a very short name, dropping `@` and domain
would generate a nonsensical name, like "me". In addition, displaying the full
name makes it possible to send emails by just guessing a popular provider such
as `@google.com`.
*/
function emailNameOnly(val) {
  return a.head(a.split(a.laxStr(val), `@`))
}

class MsgBody extends MixMsgElem(u.Elem) {
  static {dr.reg(this)}

  static mutInp(inp) {
    // Allows to find categories by tags. See `MdOptMsg` and `MdRenMsg`.
    inp.get.sets.categories.enable()
    // Allows to see message comment count.
    inp.get.sets.msgAggs.enable()
    return inp
  }

  init(ent) {
    a.reqInst(ent, d.Msg)

    E(this, A.cls(`msg-text`))
    if (ent.deletedAt) return this.deleted()

    const opt = new u.MdOptMsg(undefined, {
      // TODO: provide access to buket page, which would allow the rendered to
      // access the search text in `page.input.searchText`, which might be
      // considered more reliable / more correct than taking search from URL.
      ctx: this.ctx,
      galleryId: this.msgs().galleryId(),
    })

    const content = opt.run(ent.content)
    const emptyLink = a.find(opt.emptyLinks, a.truthy)
    if (emptyLink) this.msg().setSitePath(emptyLink)

    this.innerHTML = content
    return this
  }

  deleted() {
    this.classList.add(`fg-error`)
    E(this, {}, `(deleted)`)
    return this
  }
}

/*
TODO: on mobile screens: don't render; long tap -> dialog with actions, like
Discord mobile app.
*/
class MsgFoot extends u.Elem {
  static {dr.reg(this)}

  static mutInp(inp) {
    inp.get.sets.emoji.enable()
    inp.get.sets.markAggs.enable()
    return MsgMarks.mutInp(inp)
  }

  get A() {return A.cls(`msg-controls`)}
  pk() {return this.anc(Msg).pk()}
  marks() {return this.desc(MsgMarks)}

  init() {
    const ent = this.buk().msg(a.pk(this))
    a.reqInst(ent, d.Msg)

    const emoji = this.buk().get.sets.emoji

    return E(this, this.A,
      new MsgEmojiBtn(this).init(),
      new MsgComments(this).init(ent),
      a.vac(c.FEAT_NEW) && new MsgReply(this).init(ent),
      new MsgShare(this).init(ent),
      MsgControls(ent),
      a.vac(a.hasLen(emoji)) && new MsgMarks(this).init(),
    )
  }

  toggleMarks() {this.marks().toggle()}
}

function MixIconBtn(cls) {
  return class MixIconBtnCls extends cls {
    get A() {return A.cls(c.CLS_BTN_ICON_STD).tooltip(this.tooltip())}
    tooltip() {}
  }
}

class MsgComments extends MixIconBtn(em.Link) {
  static {dr.reg(this)}

  init(msg) {
    const count = msg.agg()?.commentCount

    return E(
      this,
      this.A.href(a.toUrl(msg.sitePath()).setHash(ID_COMMENTS)),
      count ? [s.Messages(), ElemOpt(count)] : s.MessageEmpty(),
    )
  }

  tooltip() {return `comments [c]`}
}

/*
TODO convert to details/summary. The simpler solution is to use `ShareDetails`.
However, because messages are repeated, this would bloat the markup with
mostly-unused elements. The better solution is to preserve lazy-loading and
lazy-rendering.
*/
class MsgShare extends u.Elem {
  static {dr.reg(this)}

  pk() {return this.anc(Msg).pk()}

  init() {
    return E(this, A.cls(`msg-share`), new MsgShareBtn(this).init())
  }

  async toggle() {
    const node = this.popup ||= (await u.imp(import(`./views/share.mjs`))).Share({
      ctx: this.ctx,
      pathname: d.Msg.sitePath(a.pk(this)),
      isTooltip: true,
    })

    if (node.parentNode) node.remove()
    else this.appendChild(node)
  }

  close() {this.popup?.remove()}
}

class MsgShareBtn extends MixIconBtn(eb.Btn) {
  static {dr.reg(this)}
  init() {return E(this, this.A.button(), s.Share())}
  tooltip() {return `share [s]`}
  async perform() {await this.anc(MsgShare).toggle()}
}

// Placeholder, TODO implement.
class MsgReply extends MixIconBtn(eb.Btn) {
  static {dr.reg(this)}
  init() {return E(this, this.A.button(), s.Reply())}
  tooltip() {return `reply (NYI) [t]`}
}

class MsgEmojiBtn extends MixIconBtn(eb.Btn) {
  static {dr.reg(this)}
  init() {return E(this, this.A.button(), s.EmojiPlus())}
  tooltip() {return `emoji [r]`}
  perform() {this.anc(MsgFoot).toggleMarks()}
}

class MsgMarks extends u.Elem {
  static {dr.reg(this)}

  static mutInp(inp) {
    inp.get.sets.markAggs.enable()
    return MsgMark.mutInp(inp)
  }

  get A() {return A.cls(`row-sta-str flex-wrap w-full gap-sm marks-only-active`)}
  pk() {return this.anc(Msg).pk()}

  /*
  Currently this renders the buttons for all emoji and hides some of them. This
  allows the client to toggle them without reloading the emoji data, but bloats
  the HTML markup with unused buttons. TODO either load emoji on demand when
  clicking the expand button, or dedup the emoji by serializing them in an
  ancestor such as `Msgs`.
  */
  init() {
    return E(this, this.A, a.map(
      this.buk().get.sets.emoji,
      val => new MsgMark(this).init(val),
    ))
  }

  toggle() {this.classList.toggle(`marks-only-active`)}
}

class MsgMark extends eb.Btn {
  static {dr.reg(this)}

  static mutInp(inp) {
    inp.get.sets.markAggs.enable()
    inp.get.sets.userMarkAggs.enable()
    return inp.user()
  }

  pk() {return this.anc(Msg).pk()}
  char() {return this.dataset.char}
  count() {return a.intOpt(this.dataset.count)}
  active() {return this.getAttribute(`aria-checked`) === `true`}

  init(src) {
    this.dataset.char = u.reqEmojiChar(a.pk(src))
    return this.reinit()
  }

  reinit() {
    const id = a.pk(this)
    const buk = this.buk()
    const char = this.char()

    // Uses `.getOpt` because msg is allowed to have no marks and thus no agg.
    const agg = buk.get.sets?.markAggs?.getOpt(id)
    const userAgg = buk.get.sets?.userMarkAggs?.getOpt(id)

    const count = a.vac(agg?.markPairs?.getOpt(char)?.count)
    const active = userAgg?.emojiChars?.has(char)

    return E(
      this,
      this.A.set(`aria-checked`, active).dataset({count}),
      char, ` `, count,
    )
  }

  get A() {return A.button().cls(`mark`)}

  async perform() {
    const id = a.pk(this)
    const inp = new d.BukInp()
    const inps = [{ent: {msgId: id}, emojiChar: this.char()}]

    inp.act.marks = this.active() ? {delete: inps} : {create: inps}
    inp.get.sets.markAggs.enable()
    inp.get.sets.userMarkAggs.enable()

    const buk = this.buk()

    /*
    The following code is correct but unmaintainable, fragile, bloated.
    TODO cleanup. This behavior should be implicit/automatic.
    */
    const bukNew = new d.BukOut().mut(await n.fetchBuk(this.ctx, inp))
    buk.mut(bukNew)
    if (!bukNew.get?.sets?.markAggs?.has(id)) {
      buk.get?.sets?.markAggs?.delete(id)
    }
    if (!bukNew.get?.sets?.userMarkAggs?.has(id)) {
      buk.get?.sets?.userMarkAggs?.delete(id)
    }

    this.reinit()
  }
}

function MsgControls(msg) {
  return a.vac(msg.canControl()) && [MsgEdit.init(), MsgDel.init()]
}

class MsgEdit extends MixIconBtn(eb.Btn) {
  static {dr.reg(this)}
  init() {return E(this, this.A.button(), s.Edit())}
  tooltip() {return `edit [e]`}
  perform() {this.anc(Msg).drawEdit()}
}

class MsgDel extends MixIconBtn(eb.Btn) {
  static {dr.reg(this)}
  init() {return E(this, this.A.button(), s.TrashFilled())}
  tooltip() {return `delete`}
  async perform() {await this.anc(Msg).delete()}
}

/*
TODO: when submitting msg with `@ementor`, warn the user that we're generating
an ementor response, which may take a few seconds, and they should wait.
*/
class MsgForm extends MixMsgElemObs(ef.Form) {
  static {dr.reg(this)}

  get A() {
    // We may render two forms with the same `id`.
    // This is questionable, but the HTML/DOM API does not forbid it.
    // Scrolling to hash will use the first form it finds, which suits us.
    return A.cls(`bg-white brd-1 w-full pad gap`).id(ID_COMMENTS)
  }

  init() {
    return E(
      this,
      this.A,
      new this.Textarea(this).init(),
      new this.Controls(this).init(),
    )
  }

  get Controls() {return MsgFormControls}
  controls() {return this.desc(this.Controls)}

  get Textarea() {return MsgTextarea}
  textarea() {return this.elements.namedItem(`content`)}

  trim() {this.textarea().trim()}
  getContent() {return this.trim(), this.textarea().value}
  formData() {return this.trim(), super.formData()}

  discardChanges() {
    return (
      this.getContent() === this.msg().content() ||
      window.confirm(`Discard unsaved changes?`)
    )
  }
}

/*
TODO:

  * Focus when pressing a specific global hotkey or just typing, but only when
    no input, button, or other focusable control is currently focused.
*/
class MsgFormCreate extends MsgForm {
  static {dr.reg(this)}
  get Textarea() {return MsgTextareaCreate}
  async perform() {await this.msgs().create(this, this.formData())}

  /*
  This describes the form's "affinity" to a particular message list ordering.

  If the form's affinity matches the actual list ordering, the created message
  and any other messages fetched alongside it can be neatly appended/prepended
  to the list, preserving other list elements.

  If the form's affinity doesn't match the actual list ordering, we can't simply
  append/prepend new messages, and may end up replacing the entire list.
  */
  getDesc() {throw u.errImplementInSubclass()}
}

class MsgFormCreateTop extends MsgFormCreate {
  static {dr.reg(this)}
  getDesc() {return true}
}

class MsgFormCreateBottom extends MsgFormCreate {
  static {dr.reg(this)}
  getDesc() {return false}
}

class MsgFormEdit extends MsgForm {
  static {dr.reg(this)}
  get Textarea() {return MsgTextareaEdit}
  get Controls() {return MsgFormControlsEdit}

  pk() {return a.pk(this.msg())}
  async perform() {await this.msg().update(this, this.formData())}
}

class MsgTextarea extends u.MixElemCtxSub(MixMsgElem(eim.MdTextarea)) {
  static {dr.reg(this)}

  init() {
    return E(this, {
      class: `form-textarea --small --post-mode`,
      name: `content`,
      placeholder: this.formPlaceholderText(),
    })
  }

  formPlaceholderText() {return this.msgs()?.formPlaceholderText()}

  connectedCallback() {
    super.connectedCallback()
    this.onpaste = this.onPaste
  }

  /*
  TODO consider: on deleting placeholder, cancel upload of that file.

  TODO: drag-n-drop.

  TODO consider: while uploading, pressing the submit button blocks the
  textarea and the button (or just the form) until upload is done, THEN
  submits the form.

  TODO consider displaying upload progress below the message form, with
  previews and percentages.

    ⌘V ->
    form.append(new MsgImgUpload().init(file))

    form.uploads().every(isReady)
    form.uploads().map(val => val.fileId())

    class MsgImgUpload extends u.Elem {
      constructor(file) {super().file = file}
      connectedCallback() {this.startUpload()}
      disconnectedCallback() {this.stopUpload()}
      isReady() {}
      fileId() {}
    }
  */
  async onPaste(eve) {
    const files = eve.clipboardData?.files
    if (a.isEmpty(files)) return

    /*
    Preventing the default avoids insertion of file names as text.
    If we don't prevent, we insert both the upload placeholders and
    the file names.
    */
    eve.preventDefault()

    this.clearSel()

    for (const val of files) {
      this.uploadImage(val)
      this.insert(` `) // TODO avoid trailing space.
    }
  }

  /*
  Technical note: sub-context can be used to cancel the upload requests if the
  element is unmounted while upload is in progress. At the author's machine, in
  local development, canceling this request crashes the Deno process which
  proxies the request to `/api/v2/files/images` to the API server. This crash
  seems to be caused by an internal bug in Deno. This should not be an issue in
  actual deployments because `/api` requests are routed to the API server by
  the Kube ingress, avoiding Deno. TODO recheck in a newer Deno version.

  SYNC[markdown_image_upload].
  */
  async uploadImage(val) {
    a.reqFile(val)
    const key = u.mdImgUploadPlaceholder(val.name)

    this.insert(key)
    this.resize()

    try {
      val = await n.createImageFile(this.ctxInit(), u.fileFormData(val))
      this.replaceAll(this.value.replace(key, u.mdImg(val.originalName, u.imageUrl(val))))
      this.resize()
    }
    catch (err) {
      u.onUnhandledErr(err)
      return
    }
  }
}

class MsgTextareaCreate extends MsgTextarea {
  static {dr.reg(this)}

  init() {
    if (!c.PROD && !this.value) {
      this.value = this.textContent = `Test msg ${a.uuid()}.`
    }
    return super.init()
  }
}

class MsgTextareaEdit extends MsgTextarea {
  static {dr.reg(this)}

  init() {
    this.value = a.laxStr(this.anc(Msg).content())
    return super.init()
  }

  formPlaceholderText() {return `Edit message (submit with Ctrl+Enter).`}

  connectedCallback() {
    super.connectedCallback?.()
    this.resize()
    this.focus()
  }

  onKeydown(eve) {
    super.onKeydown(eve)
    if (eve.defaultPrevented) return

    if (eve.key === c.KEY_ESCAPE) {
      a.eventKill(eve)
      this.anc(Msg).restoreView()
    }
  }
}

class MsgFormControls extends u.Elem {
  static {dr.reg(this)}

  cancel() {}

  init() {
    return E(this, A.cls(`row-bet-cen w-full gap-sm`),
      BtnBox(
        MsgFormBtnBold.init(),
        MsgFormBtnItalic.init(),
        MsgFormBtnStrike.init(),
        MsgFormBtnImg.init(),
      ),
      BtnBox(this.cancel(), MsgFormBtnSubmit.init()),
    )
  }
}

function BtnBox(...chi) {
  chi = a.compact(chi)

  return a.vac(chi) && E(
    `span`,
    A.cls(`row-cen-cen brd-1 round`),
    u.intersperse(chi, HrVer),
  )
}

function HrVer() {return E(`hr`, {class: `hr-v fg-pale`})}

class MsgFormControlsEdit extends MsgFormControls {
  static {dr.reg(this)}
  cancel() {return MsgFormBtnCancel.init()}
}

class TooltipBtn extends eb.Btn {
  static {dr.reg(this)}
  get A() {return A.button().tooltip(this.tooltip())}
  tooltip() {}
}

class MsgFormBtn extends TooltipBtn {
  static {dr.reg(this)}
  get A() {return super.A.cls(`col-cen-cen round text-center sqr-2x25 anim busy-bg-light-fg-dark`)}
  input() {return this.anc(MsgForm).textarea()}
}

class MsgFormBtnLight extends MsgFormBtn {
  static {dr.reg(this)}
  get A() {return super.A.cls(`busy-bg-light-fg-dark`)}
}

class MsgFormBtnBold extends MsgFormBtnLight {
  static {dr.reg(this)}
  init() {return E(this, this.A.cls(`wei-semi-bold`), `B`)}
  tooltip() {return `bold [Ctrl+b]`}
  perform() {this.input().bold().focus()}
}

class MsgFormBtnItalic extends MsgFormBtnLight {
  static {dr.reg(this)}
  init() {return E(this, this.A.cls(`font-italic`), `I`)}
  tooltip() {return `italic [Ctrl+i]`}
  perform() {this.input().italic().focus()}
}

class MsgFormBtnStrike extends MsgFormBtnLight {
  static {dr.reg(this)}
  init() {return E(this, this.A.cls(`dec-strike`), `S`)}
  tooltip() {return `strike-through [Ctrl+']`}
  perform() {this.input().strike().focus()}
}

class MsgFormBtnImg extends MsgFormBtnLight {
  static {dr.reg(this)}

  init() {return E(this, this.A, `img`)}
  tooltip() {return `image link`}

  perform() {
    const url = a.trim(window.prompt(`Image URL:`))
    if (!url) return
    u.validateUrlPermissive(url)
    this.input().img(url)
  }
}

class MsgFormBtnRig extends MsgFormBtnLight {
  get A() {return super.A.tooltipPos(`rig`)}
}

class MsgFormBtnCancel extends MsgFormBtnRig {
  static {dr.reg(this)}
  init() {return E(this, this.A, `Esc`)}
  tooltip() {return `cancel [Esc]`}
  perform() {this.anc(Msg).restoreView()}
}

class MsgFormBtnSubmit extends MsgFormBtnRig {
  static {dr.reg(this)}
  get A() {return super.A.submit().ssrDisabled()}
  init() {return E(this, this.A, s.Send({class: `fg-accent`}))}
  tooltip() {return `submit [Ctrl+Enter]`}
}

class MsgControlTop extends MixMsgElem(u.Elem) {
  static {dr.reg(this)}
  get A() {return A.cls(`row-cen-cen gap-sm w-full`)}

  init() {
    return E(this, this.A,
      a.vac(this.msgs()?.canSort()) && new MsgBtnOrder(this).init(),
      new MsgBtnFetchPrev(this).init(),
    )
  }

  updateByPage(page) {
    a.optInst(page, u.BukPage)
    u.elemToggleHidden(this.desc(MsgBtnFetchPrev), !page?.more)
    u.syncHiddenWithChildren(this)
    this.updateKey()
    return this
  }

  updateKey() {
    if (c.DOM_AVAILABLE && this.desc(MsgBtnFetchPrev).hidden) {
      this.msgs()?.updateKey()
    }
  }
}

class MsgControlBottom extends MixMsgElem(u.Elem) {
  static {dr.reg(this)}
  get A() {return A.cls(`row-cen-cen gap-sm w-full`)}

  init() {
    return E(this, this.A, new MsgBtnFetchNext(this).init())
  }

  updateByPage(page) {
    a.optInst(page, u.BukPage)
    u.elemToggleHidden(this.desc(MsgBtnFetchNext), !page?.more)
    u.syncHiddenWithChildren(this)
    return this
  }
}

class MsgBtnFetch extends MixMsgElem(eb.Btn) {
  static {dr.reg(this)}
  get A() {return A.button().cls(`btn-border --big round`)}
  init() {return E(this, this.A).reset()}
  reset() {return E(this, {}, `load`)}
  fetch() {}

  async perform() {
    E(this, {}, `loading...`)
    try {await this.fetch()}
    finally {this.reset()}
  }
}

class MsgBtnOrder extends MsgBtnFetch {
  static {dr.reg(this)}
  get A() {return A.button().cls(`btn-border --big round`)}

  reset() {
    return E(this, {},
      this.msgs().getDesc()
      ? [s.SortDesc(), ElemOpt(`new-to-old`)]
      : [s.SortAsc(), ElemOpt(`old-to-new`)]
    )
  }

  async fetch() {await this.anc(Msgs).reorder()}
}

class MsgBtnFetchPrev extends MsgBtnFetch {
  static {dr.reg(this)}
  reset() {return E(this, {}, `↑ `, `prev`)}
  async fetch() {await this.anc(Msgs).fetchPrev()}
}

class MsgBtnFetchNext extends u.MixElemVisObs(MsgBtnFetch) {
  static {dr.reg(this)}
  reset() {return E(this, {}, `↓ `, `next`)}
  async fetch() {await this.anc(Msgs).fetchNext()}

  onVisible() {
    if (!c.AUTO_LOAD_MORE) return
    if (this.isDisabled()) return
    if (!this.msgs().getDesc()) return
    this.onClick()
  }
}

/*
Intent: the output is either nil or a DOM element, but without unnecessary
wrapping. Some CSS behaviors like "gaps" classes require elements rather than
text. Calling this on an element should be idempotent.
*/
function ElemOpt(val) {
  if (!a.vac(val)) return undefined
  if (a.isElement(val)) return val
  return E(`span`, {}, val)
}

class MsgPlaceholder extends MixMsgElem(u.MixElem(dg.global.HTMLParagraphElement)) {
  static {dr.reg(this)}

  get A() {return A.cls(`col-cen-cen brd-1 wei-bold text-center w-full pad`)}

  init() {
    const search = this.msgs()?.searchText()
    return search ? this.initSearch(search) : this.initEmpty()
  }

  initSearch(src) {
    src = a.vac(a.laxStr(src))
    return src && E(this, this.A,
      `No messages found by the following query:`,
      E(`span`, A.cls(`bg-accent-light`), src),
    )
  }

  initEmpty() {
    const chi = this.msgs()?.listPlaceholderText()
    return a.vac(chi) && E(this, this.A, chi)
  }
}

function MsgConnection() {return s.ArrowDownward(A.cls(`font-huge fg-pale`))}

function isSubjective(val) {return a.hasMeth(val, `showSubjectPreview`)}

export class ModerationMsgs extends MixUserMsgs(MsgThreadBase) {
  static {dr.reg(this)}
  title() {return `My moderation messages`}
  pkOpt() {return a.pk(this.user())}
  getType() {return c.MSG_TYPE_MODERATION}
}

class NotifMsgs extends MixUserMsgs(Msgs) {
  get A() {return A.cls(`msgs-embed`)}
  get List() {return NotifMsgList}
  keyNext() {return `notifNext`}
  keyPrev() {return `notifPrev`}
  keyTop() {return `notifTop`}
  enableKeyStorage() {return false}
  title() {return `My notification messages`}
  getDescForced() {return true}
  pageInp(val) {
    return super.pageInp(val).mut({
      chanIds: [a.pk(this.user())],
      type: c.MSG_TYPE_NOTIF,
    })
  }
  canCreate() {return false}
  listPlaceholderText() {return `You have no notifications`}
}

export class LazyNotifMsgs extends u.MixLazyMsgs(NotifMsgs) {
  static {dr.reg(this)}
  get loaderCls() {return em.Loader}

  onVisible() {
    super.onVisible()
    this.hotkeyInit()
  }

  onInvisible() {this.hotkeyDeinit()}
}
